import { GlIcon, GlSprintf, GlModal, GlAlert, GlDisclosureDropdown, GlDisclosureDropdownGroup, GlDisclosureDropdownItem, GlButton, GlTooltipDirective } from '../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { isEmpty } from 'lodash-es';
import CanCreateProjectSnippet from 'shared_queries/snippet/project_permissions.query.graphql';
import CanCreatePersonalSnippet from 'shared_queries/snippet/user_permissions.query.graphql';
import { fetchPolicies, ERROR_POLICY_NONE } from '../../lib/graphql.js?vue3';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import axios from '../../lib/utils/axios_utils.js?vue3';
import { __, s__, sprintf } from '~/locale';
import CloneCodeDropdown from '../../vue_shared/components/code_dropdown/clone_code_dropdown.vue?vue3';
import TimeAgoTooltip from '../../vue_shared/components/time_ago_tooltip.vue?vue3';
import { createAlert, VARIANT_DANGER, VARIANT_SUCCESS } from '../../alert.js?vue3';
import { VISIBILITY_LEVEL_PUBLIC_STRING } from '~/visibility_level/constants';
import ImportedBadge from '../../vue_shared/components/imported_badge.vue?vue3';
import SafeHtml from '~/vue_shared/directives/safe_html';
import { newProjectSnippetPath, editProjectSnippetPath, projectSnippetsPath } from '~/lib/utils/path_helpers/project';
import { newSnippetPath, editSnippetPath } from '~/lib/utils/path_helpers/snippets';
import { dashboardSnippetsPath } from '~/lib/utils/path_helpers/dashboard';
import DeleteSnippetMutation from '../mutations/delete_snippet.mutation.graphql';
export const i18n = {
    snippetSpamSuccess: sprintf(s__('Snippets|%{spammable_titlecase} was submitted to Akismet successfully.'), {
        spammable_titlecase: __('Snippet')
    }),
    snippetSpamFailure: s__('Snippets|Error with Akismet. Please check the logs for more info.'),
    hiddenTooltip: s__('Snippets|This snippet is hidden because its author has been banned'),
    hiddenAriaLabel: __('Hidden'),
    snippetAction: s__('Snippets|Snippet actions')
};
export default {
    name: 'SnippetHeader',
    components: {
        CloneCodeDropdown,
        GlIcon,
        GlSprintf,
        GlModal,
        GlAlert,
        GlButton,
        GlDisclosureDropdown,
        GlDisclosureDropdownGroup,
        GlDisclosureDropdownItem,
        TimeAgoTooltip,
        ImportedBadge
    },
    directives: {
        GlTooltip: GlTooltipDirective,
        SafeHtml
    },
    apollo: {
        canCreateSnippet: {
            fetchPolicy: fetchPolicies.NO_CACHE,
            errorPolicy: ERROR_POLICY_NONE,
            query () {
                return this.snippet.project ? CanCreateProjectSnippet : CanCreatePersonalSnippet;
            },
            variables () {
                return {
                    fullPath: this.snippet.project ? this.snippet.project.fullPath : undefined
                };
            },
            update (data) {
                return this.snippet.project ? data.project?.userPermissions.createSnippet : data.currentUser?.userPermissions.createSnippet;
            }
        }
    },
    inject: [
        'reportAbusePath',
        'canReportSpam'
    ],
    props: {
        snippet: {
            type: Object,
            required: true
        }
    },
    data () {
        return {
            isLoading: false,
            isSubmittingSpam: false,
            errorMessage: '',
            canCreateSnippet: false,
            isDeleteModalVisible: false,
            isDropdownShown: false
        };
    },
    computed: {
        snippetHasBinary () {
            return Boolean(this.snippet.blobs.find((blob)=>blob.binary));
        },
        authoredMessage () {
            return this.snippet.author ? __('Authored %{timeago} by %{author}') : __('Authored %{timeago}');
        },
        editItem () {
            return {
                text: __('Edit'),
                href: this.editLink,
                disabled: this.snippetHasBinary,
                title: this.snippetHasBinary ? __('Snippets with non-text files can only be edited via Git.') : undefined,
                extraAttrs: {
                    class: '@sm/panel:!gl-hidden'
                }
            };
        },
        canReportSpaCheck () {
            return this.canReportSpam && !isEmpty(this.reportAbusePath);
        },
        spamItem () {
            return {
                text: __('Submit as spam'),
                action: ()=>this.submitAsSpam()
            };
        },
        deleteItem () {
            return {
                text: __('Delete'),
                action: ()=>this.showDeleteModal(),
                variant: 'danger'
            };
        },
        newSnippetItem () {
            return {
                text: __('New snippet'),
                href: this.snippet.project ? newProjectSnippetPath(this.snippet.project.fullPath) : newSnippetPath()
            };
        },
        hasAdminSnippetPermission () {
            return Boolean(this.snippet.userPermissions?.adminSnippet);
        },
        hasUpdateSnippetPermission () {
            return Boolean(this.snippet.userPermissions?.updateSnippet);
        },
        showActionDropdown () {
            return this.hasUpdateSnippetPermission || this.canCreateSnippet || this.canReportSpaCheck || this.hasAdminSnippetPermission;
        },
        hasPersonalSnippetActions () {
            return this.showActionDropdown || this.canBeCloned;
        },
        editLink () {
            const id = getIdFromGraphQLId(this.snippet.id);
            return this.snippet.project ? editProjectSnippetPath(this.snippet.project.fullPath, id) : editSnippetPath(id);
        },
        visibility () {
            return this.snippet.visibilityLevel;
        },
        snippetVisibilityLevelDescription () {
            switch(this.visibility){
                case 'private':
                    return this.snippet.project !== null ? __('The snippet is visible only to project members.') : __('The snippet is visible only to me.');
                case 'internal':
                    return __('The snippet is visible to any logged in user except external users.');
                default:
                    return __('The snippet can be accessed without any authentication.');
            }
        },
        visibilityLevelIcon () {
            switch(this.visibility){
                case 'private':
                    return 'lock';
                case 'internal':
                    return 'shield';
                default:
                    return 'earth';
            }
        },
        showDropdownTooltip () {
            return !this.isDropdownShown ? this.$options.i18n.snippetAction : '';
        },
        isInPrivateProject () {
            const projectVisibility = this.snippet?.project?.visibility;
            const isLimitedVisibilityProject = projectVisibility !== VISIBILITY_LEVEL_PUBLIC_STRING;
            return projectVisibility ? isLimitedVisibilityProject : false;
        },
        embeddable () {
            return this.visibility === VISIBILITY_LEVEL_PUBLIC_STRING && !this.isInPrivateProject;
        },
        canBeCloned () {
            return Boolean(this.snippet.sshUrlToRepo || this.snippet.httpUrlToRepo);
        },
        canBeClonedOrEmbedded () {
            return this.canBeCloned || this.embeddable;
        }
    },
    methods: {
        redirectToSnippets () {
            window.location.pathname = this.snippet.project ? projectSnippetsPath(this.snippet.project.fullPath) : dashboardSnippetsPath();
        },
        closeDeleteModal () {
            this.isDeleteModalVisible = false;
        },
        showDeleteModal () {
            this.isDeleteModalVisible = true;
        },
        onShowDropdown () {
            this.isDropdownShown = true;
        },
        onHideDropdown () {
            this.isDropdownShown = false;
        },
        deleteSnippet () {
            this.isLoading = true;
            this.$apollo.mutate({
                mutation: DeleteSnippetMutation,
                variables: {
                    id: this.snippet.id
                }
            }).then(({ data })=>{
                if (data?.destroySnippet?.errors.length) {
                    throw new Error(data?.destroySnippet?.errors[0]);
                }
                this.errorMessage = undefined;
                this.closeDeleteModal();
                this.redirectToSnippets();
            }).catch((err)=>{
                this.isLoading = false;
                this.errorMessage = err.message;
            }).finally(()=>{
                this.isLoading = false;
            });
        },
        async submitAsSpam () {
            try {
                this.isSubmittingSpam = true;
                await axios.post(this.reportAbusePath);
                createAlert({
                    message: this.$options.i18n.snippetSpamSuccess,
                    variant: VARIANT_SUCCESS
                });
            } catch (error) {
                createAlert({
                    message: this.$options.i18n.snippetSpamFailure,
                    variant: VARIANT_DANGER
                });
            } finally{
                this.isSubmittingSpam = false;
            }
        }
    },
    i18n
};
